import { PaginationState } from './types';

/**
 * Retrieves and validates pagination state from localStorage.
 * If parsing fails, validation fails, or no data exists, returns the default
 * pagination state using the provided initial values.
 *
 * @param key - The localStorage key where pagination state is stored
 * @param initialPage - Default page number to use if stored state is invalid (0-indexed)
 * @param initialSize - Default page size to use if stored state is invalid
 * @returns The validated pagination state from storage or defaults
 */
export const getStoredPagination = (
    key: string | undefined,
    initialPage: number,
    initialSize: number
): PaginationState => {
    // If no key provided, skip localStorage and return defaults
    if (!key) {
        return { page: initialPage, size: initialSize, totalRows: 0, totalPages: 0 };
    }

    try {
        const saved = localStorage.getItem(key);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (
                typeof parsed.page === 'number' &&
                parsed.page >= 0 &&
                typeof parsed.size === 'number' &&
                parsed.size > 0
            ) {
                return {
                    page: parsed.page,
                    size: parsed.size,
                    totalRows: parsed.totalRows ?? 0,
                    totalPages: parsed.totalPages ?? 0,
                };
            }
        }
    } catch (e) {
        console.error(`[usePagination] Failed to parse localStorage for key "${key}":`, e);
    }
    return { page: initialPage, size: initialSize, totalRows: 0, totalPages: 0 };
};

/**
 * Persists pagination state to localStorage.
 *
 * Serializes the pagination state as JSON and stores it under the specified key.
 * Silently logs errors to console if storage fails.
 *
 * @param key - The localStorage key to store pagination state under
 * @param pagination - The pagination state object to persist
 */
export const savePaginationToStorage = (
    key: string,
    pagination: PaginationState
): void => {
    try {
        localStorage.setItem(key, JSON.stringify(pagination));
    } catch (e) {
        console.error(`[usePagination] Failed to save to localStorage for key "${key}":`, e);
    }
};
